import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import React, { useState } from 'react';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// Define unique colors for each nav item
const selectedColors = {
  'dashboard': 'rgba(76, 175, 80, 0.5)',        // Green
  'schedule-posts': 'rgba(28, 104, 185, 1)',    // Blue
  'all': 'rgba(28, 104, 185, 0.7)',             // Blue lighter
  'instagram': 'rgba(225, 48, 108, 0.8)',       // Instagram pink
  'youtube': 'rgba(255, 0, 0, 0.8)',            // YouTube red
  'facebook': 'rgba(24, 119, 242, 0.8)',        // Facebook blue
  'linkedin': 'rgba(0, 119, 181, 0.8)',         // LinkedIn blue
  'messages': 'rgba(185, 26, 26, 0.9)',         // Red
  'accounts': 'rgba(156, 39, 176, 0.5)',        // Purple
  'users': 'rgba(255, 152, 0, 0.5)',            // Orange
  'plans': 'rgba(0, 188, 212, 0.5)',            // Cyan
  'platform': 'rgba(233, 30, 99, 0.5)',         // Pink
  'default': 'rgba(255, 255, 255, 0.19)'        // White for others
};

export default function NavItem({ item, level, selectedId, setSelectedId }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [open, setOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  // Check if this item is selected
  const isSelected = selectedId === item.id;

  const handleClick = () => {
    if (hasChildren) {
      setOpen(!open);
    } else {
      // Only navigate and select if no children
      if (downLG) handlerDrawerOpen(false);
      setSelectedId(item.id);
    }
  };

  // Icon logic - FIXED: removed duplicate fontSize
const Icon = item.icon;
const itemIcon = Icon ? <Icon style={{ fontSize: drawerOpen ? '1.25rem' : '1.5rem' }} /> : null;


  // Badge rendering
  const span = item.badge;
  const spanElement = span ? (
    <span
      style={{
        backgroundColor: '#990F02',
        color: 'white',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.65rem',
        display: 'inline-block',
        lineHeight: 1.2,
        marginLeft: '8px'
      }}
    >
      {span.text}
    </span>
  ) : null;

  // Get unique color for this item
  const selectedBgColor = selectedColors[item.id] || selectedColors.default;


  return (
    <Box sx={{ position: 'relative' }}>
      <ListItemButton
        component={hasChildren ? 'div' : Link}
        to={hasChildren ? undefined : item.url}
        onClick={handleClick}
        sx={{
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: 1,
          mx: '4%',
          my: 0.5,
          borderRadius: '8px',
          transition: 'all 0.3s ease',
          backgroundColor: isSelected ? selectedBgColor : 'transparent',
          '&:hover': {
            backgroundColor: selectedBgColor,
            transform: 'translateX(4%)'
          }
        }}
      >
        {itemIcon && (
          <ListItemIcon sx={{ minWidth: 32, color: isSelected ? '#fff' : '#bbb' }}>
            {itemIcon}
          </ListItemIcon>
        )}
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ color: isSelected ? '#fff' : '#bbb', fontWeight: isSelected ? 600 : 400 }}>
                  {item.title}
                </Typography>
                {spanElement}
              </Box>
              {hasChildren && (
                <Box sx={{ color: '#bbb', ml: 1, display: 'flex', alignItems: 'center' }}>
                  {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
              )}
            </Box>
          }
        />
      </ListItemButton>
      
      {/* Render children if they exist */}
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map((child) => (
              <NavItem
                key={child.id}
                item={child}
                level={level + 1}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
              />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.object.isRequired,
  level: PropTypes.number.isRequired,
  selectedId: PropTypes.string.isRequired,
  setSelectedId: PropTypes.func.isRequired,
};
